import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { FileText, Pencil, Search, Trash2 } from 'lucide-react'
import { useAuth } from '@chainlit/react-client'
import { useMallarFetch } from '@/hooks/useMallarFetch'
import { useMallarCheckout } from '@/hooks/useMallarCheckout'
import { Loader } from '@/components/Loader'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import Page from 'pages/Page'
import { TemplateDetailModal } from '@/components/mallar/TemplateDetailModal'
import type { TemplateMeta } from '@/components/mallar/TemplateDetailModal'
import { PurchaseSuccessModal } from '@/components/mallar/PurchaseSuccessModal'

interface Contract {
  id: string
  template_id: string
  title?: string
  phase: string
  filled_count: number
  updated_at: string
}

function ContractsContent() {
  const apiFetch = useMallarFetch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const isAdmin =
    (user as any)?.metadata?.role === 'ADMIN' ||
    (user as any)?.role === 'ADMIN'
  const { buyTemplate, pendingId } = useMallarCheckout()
  const [contracts, setContracts] = useState<Contract[]>([])
  const [templates, setTemplates] = useState<TemplateMeta[]>([])
  const [owned, setOwned] = useState<Record<string, boolean>>({})
  const [adminFree, setAdminFree] = useState(false)
  const [loading, setLoading] = useState(true)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [creating, setCreating] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateMeta | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [successModal, setSuccessModal] = useState<{ templateId: string; sessionId: string } | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')

  const canCreate = (id: string) => isAdmin || adminFree || owned[id] === true

  const loadSummary = () => {
    apiFetch('/api/purchases/summary')
      .then(r => r.json())
      .then(d => { if (d && (d._admin || d._free)) setAdminFree(true); else setOwned(d || {}) })
      .catch(() => {})
  }

  const startRename = (c: Contract) => {
    setRenamingId(c.id)
    setRenameValue(c.title || '')
    setTimeout(() => renameInputRef.current?.select(), 0)
  }

  const commitRename = useCallback(async (id: string) => {
    const trimmed = renameValue.trim()
    setRenamingId(null)
    if (!trimmed) return
    setContracts(prev => prev.map(c => c.id === id ? { ...c, title: trimmed } : c))
    await apiFetch(`/api/contracts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title: trimmed })
    })
  }, [apiFetch, renameValue])

  useEffect(() => {
    apiFetch('/api/contracts')
      .then(r => r.json())
      .then(data => { setContracts(Array.isArray(data) ? data : []); setLoading(false) })
      .catch(() => setLoading(false))
    fetch('/mallar/api/templates')
      .then(r => r.json())
      .then(data => setTemplates(Array.isArray(data) ? data : []))
      .catch(() => {})
    loadSummary()
  }, [])

  const startContract = async (templateId: string) => {
    if (creating) return
    setCreating(templateId)
    try {
      const res = await apiFetch('/api/contracts', { method: 'POST', body: JSON.stringify({ template_id: templateId }) })
      if (res.status === 402) {
        await buyTemplate(templateId)
        return
      }
      if (!res.ok) {
        toast.error('Kunde inte skapa avtalet.')
        return
      }
      const data = await res.json()
      navigate(`/mallar/contract/${data.contract_id}`)
    } finally {
      setCreating(null)
    }
  }

  const deleteContract = useCallback(async (id: string) => {
    await apiFetch(`/api/contracts/${id}`, { method: 'DELETE' })
    setContracts(prev => prev.filter(c => c.id !== id))
    setConfirmId(null)
  }, [apiFetch])

  const pct = (c: Contract) => Math.min(Math.round((c.filled_count / 20) * 100), 100)

  const categories = useMemo(() => {
    const seen = new Set<string>()
    templates.forEach(t => { if (t.category) seen.add(t.category) })
    return Array.from(seen).sort()
  }, [templates])

  const filteredTemplates = useMemo(() => {
    const q = searchQuery.toLowerCase()
    return templates.filter(t => {
      const matchesSearch = !q ||
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q)
      const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter
      return matchesSearch && matchesCategory
    })
  }, [templates, searchQuery, categoryFilter])

  const openModal = (t: TemplateMeta) => {
    setSelectedTemplate(t)
    setModalOpen(true)
  }

  // Handle return from Stripe checkout (?purchase=success|cancelled&template=...&session_id=...)
  useEffect(() => {
    const p = searchParams.get('purchase')
    if (!p) return
    if (p === 'success') {
      const tid = searchParams.get('template')
      const sid = searchParams.get('session_id')
      if (tid && sid) {
        setSuccessModal({ templateId: tid, sessionId: sid })
      }
      loadSummary()
    } else if (p === 'cancelled') {
      toast('Köpet avbröts.')
    }
    const next = new URLSearchParams(searchParams)
    next.delete('purchase')
    next.delete('template')
    next.delete('session_id')
    setSearchParams(next, { replace: true })
  }, [searchParams])

  return (
    <>
    <ScrollArea className="flex-1 h-full">
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-10">

        {/* My contracts */}
        <section>
          <h2 className="text-sm font-semibold text-foreground mb-4">Mina avtal</h2>
          {loading ? (
            <div className="flex justify-center py-10"><Loader className="h-6 w-6" /></div>
          ) : contracts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">Du har inga avtal än. Skapa ett nedan.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {contracts.map(c => (
                <Card key={c.id} className="flex flex-col gap-0">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <Badge variant="outline" className="text-[10px] font-medium capitalize">
                        {c.template_id.replace('avtal', ' avtal')}
                      </Badge>
                      {c.phase === 'complete' && (
                        <Badge className="text-[10px] bg-green-600 hover:bg-green-600">Klart</Badge>
                      )}
                    </div>
                    {renamingId === c.id ? (
                      <Input
                        ref={renameInputRef}
                        className="h-7 text-sm mt-2 px-1 py-0"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onBlur={() => commitRename(c.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') commitRename(c.id)
                          if (e.key === 'Escape') setRenamingId(null)
                        }}
                        autoFocus
                      />
                    ) : (
                      <div className="flex items-center gap-1 mt-2 group/title">
                        <CardTitle className="text-sm">{c.title || 'Namnlöst avtal'}</CardTitle>
                        <button
                          className="opacity-0 group-hover/title:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                          onClick={e => { e.stopPropagation(); startRename(c) }}
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    <CardDescription className="text-xs">
                      {new Date(c.updated_at).toLocaleDateString('sv-SE')} · {c.filled_count} fält ifyllda
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pb-3">
                    <Progress value={pct(c)} className={cn('h-1.5', c.phase === 'complete' && '[&>div]:bg-green-500')} />
                  </CardContent>
                  <CardFooter className="pt-0 flex items-center gap-2">
                    <Button
                      size="sm"
                      className="flex-1 h-8 text-xs"
                      onClick={() => navigate(`/mallar/contract/${c.id}`)}
                    >
                      {c.phase === 'complete' ? 'Visa avtal' : 'Fortsätt'}
                    </Button>
                    {confirmId === c.id ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-red-600">Radera? Köpet återbetalas ej.</span>
                        <Button size="sm" className="h-7 text-xs px-2 bg-red-600 hover:bg-red-700 text-white border-0" onClick={() => deleteContract(c.id)}>Ja</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-foreground" onClick={() => setConfirmId(null)}>Nej</Button>
                      </div>
                    ) : (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => setConfirmId(c.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </section>

        <Separator />

        {/* Create new */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-sm font-semibold text-foreground">Skapa nytt avtal</h2>
          </div>

          {/* Search + filter */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-8 h-8 text-xs"
                placeholder="Sök mallar..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-44 h-8 text-xs">
                <SelectValue placeholder="Alla kategorier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla kategorier</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {filteredTemplates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">Inga mallar hittades.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredTemplates.map(t => (
                <Card
                  key={t.id}
                  className="flex flex-col cursor-pointer hover:ring-1 hover:ring-ring transition-shadow"
                  onClick={() => openModal(t)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                      {t.category && (
                        <Badge variant="secondary" className="text-[10px] font-medium">{t.category}</Badge>
                      )}
                      {owned[t.id] && (
                        <Badge className="text-[10px] bg-green-600 hover:bg-green-600 ml-auto">Köpt</Badge>
                      )}
                    </div>
                    <CardTitle className="text-sm">{t.title}</CardTitle>
                    <CardDescription className="text-xs leading-relaxed">{t.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </ScrollArea>

    <TemplateDetailModal
      template={selectedTemplate}
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      onStart={async (id) => {
        await startContract(id)
        setModalOpen(false)
      }}
      onBuy={(id) => buyTemplate(id)}
      starting={creating === selectedTemplate?.id}
      purchasing={pendingId === selectedTemplate?.id}
      canCreate={selectedTemplate ? canCreate(selectedTemplate.id) : false}
      owned={selectedTemplate ? owned[selectedTemplate.id] === true : false}
    />
    <PurchaseSuccessModal
      open={!!successModal}
      templateId={successModal?.templateId ?? null}
      sessionId={successModal?.sessionId ?? null}
      onCreateContract={async (id) => {
        setSuccessModal(null)
        await startContract(id)
      }}
      onClose={() => setSuccessModal(null)}
    />
    </>
  )
}

export default function MallarContracts() {
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user !== undefined && !user) navigate('/', { replace: true })
  }, [user, navigate])

  if (user === undefined) return null

  return (
    <Page>
      <ContractsContent />
    </Page>
  )
}
